import firebase from '@/firebase';
import api from '@/store/helpers/api';
import i18n from '@/i18n';

const auth = firebase.auth();
const EMAIL_DOMAIN = process.env.VUE_APP_EMAIL_DOMAIN || '';

function emailFromName(name) {
  return `${name}@${EMAIL_DOMAIN}`.toLowerCase();
}

const initialState = {
  isLogged: false,
  isLoading: false,
  name: '',
  id: '',
  isAnonymous: false,
  justLoggedOut: false, // This is to avoid a login-logout loop.
};

const getters = {};

const actions = {
  anonymousLogin({ commit, dispatch }) {
    return auth.signInAnonymously()
      .then(() => {
        commit('SET', { name: 'you' });
        return dispatch('auth');
      });
  },
  login({ commit, dispatch, state }, { name, pass }) {
    if (state.isLoading) {
      return false;
    }

    commit('LOADING');
    const email = emailFromName(name);

    return auth.signInWithEmailAndPassword(email, pass)
      // .then((data) => {
      //   return data;
      // })
      .then(() => commit('LOADEND'))
      .then(() => dispatch('auth'))
      .catch((e) => {
        commit('LOADEND');

        dispatch('alerts/show', {
          color: 'error',
          text: i18n.t('LOGIN.ERROR'),
        }, { root: true });

        console.error(e);
      });
  },
  register({ state, dispatch, commit }, {
    name,
    pass,
    year,
    agree,
  }) {
    if (state.isLoading) {
      return false;
    }

    commit('LOADING');

    const email = emailFromName(name);
    const credential = firebase.auth.EmailAuthProvider.credential(email, pass);

    return auth.currentUser.linkWithCredential(credential)
    // return auth.createUserWithEmailAndPassword(email, pass)
      .then((usercred) => {
        const { user } = usercred;

        return api.post('auth/register', {
          email,
          name,
          year,
          agree,
          id: user.uid,
        });
      })
      .then(() => commit('LOADEND'))
      .then(() => dispatch('auth'))
      .finally(() => commit('LOADEND'));
  },
  auth({ commit, dispatch, state }) {
    if (state.isLoading || !auth.currentUser) {
      return false;
    }

    commit('LOADING');
    return auth.currentUser.getIdToken()
      .then((token) => api.post('/auth/login', { token }))
      .then((user) => {
        if (user && user.token) {
          commit('LOGIN', user.token);
          commit('SET', user.account);
          dispatch('settings/set', user.account, { root: true });
        }
        return user.token;
      })
      .finally(() => {
        commit('LOADEND');
      })
      .catch((error) => {
        console.error(error.code, error.message);
      });
  },
  logout({ commit, dispatch }) {
    return auth.signOut()
      .then(() => {
        commit('LOG_OUT');
        return Promise.all([
          dispatch('rondo/reset', null, { root: true }),
          dispatch('cards/reset', null, { root: true }),
          dispatch('quiz/reset', null, { root: true }),
        ]);
      })
      .catch((error) => {
        console.error(error);
      });
  },
  resetPass({ commit, dispatch, state }, { name, year, pass }) {
    if (state.isLoading) {
      return false;
    }

    commit('LOADING');
    const email = emailFromName(name);

    return api.post('/auth/reset-token', { email, year })
      // Ask for token
      .then((res) => res.token)
      // Send new pass
      .then((token) => api.post('/auth/reset', {
        email,
        token,
        password: pass,
      }))
      .then(() => commit('LOADEND'))
      // Login
      .then(() => dispatch('login', { name, pass }))
      .catch(() => {
        commit('LOADEND');

        dispatch('alerts/show', {
          color: 'error',
          text: i18n.t('RESET_PASS.ERROR'),
        }, { root: true });
      });
  },
  justLoggedIn({ commit }) {
    commit('JUSTLOGGEDIN');
  },
  justLoggedOut({ commit }) {
    commit('JUSTLOGGEDOUT');
  },
  deleteAccount({ commit }) {
    return api.delete('/auth')
      .finally(() => {
        commit('LOG_OUT');
      });
  },
};

const mutations = {
  LOADING(state) {
    state.isLoading = true;
  },
  LOADEND(state) {
    state.isLoading = false;
  },
  LOGIN(state, token) {
    api.defaults.headers.common = { Authorization: `Bearer ${token}` };
    state.isLogged = true;
  },
  SET_PIC(state, pic) {
    state.pic = pic;
  },
  SET(state, data) {
    state.isLogged = true;
    state.id = data.id;
    state.name = data.name;
    state.pic = data.pic;
    state.isAnonymous = !data.role || data.role === 'anonymous';
  },
  LOG_OUT(state) {
    api.defaults.headers.common = { Authorization: '' };
    state.isLogged = false;
    state.justLoggedOut = true;
    state.id = '';
    state.name = '';
    state.pic = '';
    state.isAnonymous = null;
  },
  JUSTLOGGEDIN(state) {
    state.justLoggedOut = false;
  },
  JUSTLOGGEDOUT(state) {
    state.justLoggedOut = true;
  },
};

// Export store
export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
